import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '@/config/api';

const AuthContext = createContext(null);

// Set up axios interceptor to always include token from localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Set up response interceptor to handle 401 errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401 error, clear the invalid token and reload
    if (error.response?.status === 401) {
      console.warn('🔒 Received 401 Unauthorized - clearing invalid token');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      // Reload to trigger login flow
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    return storedToken;
  });
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setLoading(false);
      if (token) {
        setToken(null);
        setUser(null);
      }
      return;
    }
    
    try {
      // Ensure token is set in axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      // Sync token state if it changed
      if (currentToken !== token) {
        setToken(currentToken);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        headers: error.config?.headers
      });
      // Only logout if it's a 401 (unauthorized), not other errors
      if (error.response?.status === 401) {
        console.warn('Authentication failed, logging out');
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    console.log('🔐 Login attempt to:', `${API_URL}/auth/login`);
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    console.log('✅ Login successful, token received');
    
    // Store token and update state synchronously
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Update state - this will trigger fetchUser via useEffect
    setToken(access_token);
    setUser(userData);
    
    return { user: userData, token: access_token };
  };

  const register = async (name, email, password) => {
    console.log('📝 Register attempt to:', `${API_URL}/auth/register`);
    const response = await axios.post(`${API_URL}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    console.log('✅ Registration successful, token received');
    
    // Store token and update state synchronously
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Update state - this will trigger fetchUser via useEffect
    setToken(access_token);
    setUser(userData);
    
    return { user: userData, token: access_token };
  };


  // Update user data (call after profile update)
  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  // Refresh user from server
  const refreshUser = () => {
    if (token) {
      fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateUser,
      refreshUser,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
